import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../auth/AuthProvider';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type ModalState = 'idle' | 'loading' | 'sent' | 'error';

const RESEND_COOLDOWN = 30; // seconds

export const AuthModal: React.FC<AuthModalProps> = ({ isOpen, onClose }) => {
  const { signInWithOtp } = useAuth();
  const [email, setEmail] = useState('');
  const [state, setState] = useState<ModalState>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const [resendCountdown, setResendCountdown] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Focus input on open
  useEffect(() => {
    if (isOpen && state === 'idle') {
      setTimeout(() => inputRef.current?.focus(), 80);
    }
  }, [isOpen, state]);

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  // Resend countdown
  const startResendCountdown = useCallback(() => {
    setResendCountdown(RESEND_COOLDOWN);
    timerRef.current = setInterval(() => {
      setResendCountdown(prev => {
        if (prev <= 1) {
          if (timerRef.current) clearInterval(timerRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  useEffect(() => {
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, []);

  // Reset on close
  useEffect(() => {
    if (!isOpen) {
      // Small delay so the exit animation doesn't flash reset state
      const t = setTimeout(() => {
        setState('idle');
        setEmail('');
        setErrorMsg('');
        setResendCountdown(0);
        if (timerRef.current) clearInterval(timerRef.current);
      }, 200);
      return () => clearTimeout(t);
    }
  }, [isOpen]);

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!email.trim() || state === 'loading') return;

    // Basic email validation
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setState('error');
      setErrorMsg('Please enter a valid email address.');
      return;
    }

    setState('loading');
    setErrorMsg('');

    try {
      await signInWithOtp(email.trim().toLowerCase());
      setState('sent');
      startResendCountdown();
    } catch (err: any) {
      setState('error');
      const msg = err?.message || '';
      if (msg.includes('rate limit') || msg.includes('too many')) {
        setErrorMsg('Too many attempts. Please wait a few minutes and try again.');
      } else if (msg.includes('invalid') || msg.includes('email')) {
        setErrorMsg('Invalid email address. Please check and try again.');
      } else {
        setErrorMsg('Something went wrong. Please try again.');
      }
    }
  };

  const handleResend = () => {
    if (resendCountdown > 0) return;
    setState('idle');
    setErrorMsg('');
    // Trigger resend
    setTimeout(() => handleSubmit(), 50);
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fs-modal-backdrop" onClick={handleBackdropClick} role="dialog" aria-modal="true">
      <div className="fs-modal">

        {/* Close */}
        <button className="fs-modal__close" onClick={onClose} aria-label="Close">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M2 2L14 14M14 2L2 14" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round"/>
          </svg>
        </button>

        {/* Logo */}
        <div className="fs-modal__logo">
          <img
            src="/src/assets/FORMA-Studio_logo_forma_icon_w.png"
            alt="FORMA Studio"
            className="fs-modal__logo-icon"
          />
          <span className="fs-modal__logo-name">FORMA Studio</span>
          <span className="fs-badge fs-badge--beta" style={{ fontSize: 10 }}>Beta</span>
        </div>

        {/* ── State: idle / loading / error ── */}
        {(state === 'idle' || state === 'loading' || state === 'error') && (
          <form onSubmit={handleSubmit} noValidate>
            <h2 className="fs-modal__headline">Join the beta.</h2>
            <p className="fs-modal__subline">Enter your email. We'll send you a link — no password needed.</p>

            <div className="fs-modal__input-wrap">
              <input
                ref={inputRef}
                type="email"
                className={`fs-modal__input${state === 'error' ? ' fs-modal__input--error' : ''}`}
                placeholder="your@email.com"
                value={email}
                onChange={e => {
                  setEmail(e.target.value);
                  if (state === 'error') { setState('idle'); setErrorMsg(''); }
                }}
                disabled={state === 'loading'}
                autoComplete="email"
              />
            </div>

            {state === 'error' && errorMsg && (
              <p className="fs-modal__error">
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{ flexShrink: 0 }}>
                  <circle cx="6" cy="6" r="5.5" stroke="#EF4444"/>
                  <path d="M6 3.5V6.5M6 8.5V8.51" stroke="#EF4444" strokeWidth="1.25" strokeLinecap="round"/>
                </svg>
                {errorMsg}
              </p>
            )}

            <button
              type="submit"
              className="fs-modal__cta"
              disabled={state === 'loading' || !email.trim()}
            >
              {state === 'loading' ? 'Sending…' : 'Send magic link'}
            </button>

            <p className="fs-modal__fine">Free account. No credit card. No trial period.</p>
          </form>
        )}

        {/* ── State: sent ── */}
        {state === 'sent' && (
          <div>
            <div className="fs-modal__sent-icon">
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
                <rect x="2" y="4" width="20" height="16" rx="3" stroke="currentColor" strokeWidth="1.5"/>
                <path d="M2 7L12 13L22 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
            </div>
            <h2 className="fs-modal__sent-title">Check your inbox.</h2>
            <p className="fs-modal__sent-body">
              We sent a magic link to{' '}
              <span className="fs-modal__sent-email">{email}</span>.
              <br />
              Click it to sign in — no password needed.
            </p>
            <span
              className={`fs-modal__resend${resendCountdown === 0 ? ' fs-modal__resend--active' : ''}`}
              onClick={handleResend}
              role={resendCountdown === 0 ? 'button' : undefined}
              tabIndex={resendCountdown === 0 ? 0 : undefined}
              onKeyDown={e => e.key === 'Enter' && resendCountdown === 0 && handleResend()}
            >
              {resendCountdown > 0
                ? `Resend in ${resendCountdown}s`
                : 'Resend link'}
            </span>
          </div>
        )}

      </div>
    </div>
  );
};
